import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'
import { columnCardsQueryKey } from '../hooks/useColumnCards'
import ColumnFieldsConfigModal from './ColumnFieldsConfigModal'

function ColumnHeader({ column, headerRef, totalCount, unseenCount, searchActive }) {
  const { canManage, canEdit, selectedBoardId, currentBoard } = useBoardsContext()
  const { createColumn, updateColumn, deleteColumn, clearColumnCards, markColumnRead } = useBoardMutations(selectedBoardId)
  const queryClient = useQueryClient()
  const columnIndex = currentBoard?.columns?.findIndex(c => c.id === column.id) ?? 0
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(column.name)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showFieldsModal, setShowFieldsModal] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const handleSave = () => {
    if (name.trim() && name.trim() !== column.name) {
      updateColumn.mutate({ id: column.id, name: name.trim() })
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') { setName(column.name); setIsEditing(false) }
  }

  const handleInsertBefore = () => {
    setMenuOpen(false)
    createColumn.mutate({ name: 'New Column', row_order_position: columnIndex })
  }

  const handleInsertAfter = () => {
    setMenuOpen(false)
    createColumn.mutate({ name: 'New Column', row_order_position: columnIndex + 1 })
  }

  const handleClearCards = () => {
    setMenuOpen(false)
    if (totalCount === 0) return
    if (!window.confirm(`Remove all ${totalCount} card(s) from "${column.name}"?`)) return
    clearColumnCards.mutate(column.id)
  }

  const handleDelete = () => {
    setMenuOpen(false)
    if (totalCount > 0) {
      if (!window.confirm(`Delete "${column.name}" and its ${totalCount} card(s)?`)) return
    }
    deleteColumn.mutate(column.id)
  }

  const handleConfigureFields = () => {
    setMenuOpen(false)
    setShowFieldsModal(true)
  }

  const handleSaveFields = (tokens) => {
    return updateColumn.mutateAsync({ id: column.id, card_field_config: tokens })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: columnCardsQueryKey(column.id) })
      })
  }

  return (
    <div className="kb-column-header">
      {canManage && (
        <div ref={headerRef} className="kb-col-drag-handle">
          <span /><span /><span />
        </div>
      )}
      {isEditing ? (
        <input
          type="text"
          className="form-input text-sm rounded border-gray-300"
          style={{ flex: 1 }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      ) : (
        <>
          <span className="kb-col-name" onDoubleClick={() => canManage && setIsEditing(true)}>
            {column.name}
          </span>
          <span
            className="kb-col-count"
            title={searchActive ? `${totalCount} match(es) in this column` : `${totalCount} cards`}
          >
            {totalCount}
          </span>
          {unseenCount > 0 && (
            <button
              className="px-1.5 py-0.5 rounded-md text-xs font-semibold leading-4 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 focus:outline-none"
              onClick={() => markColumnRead.mutate(column.id)}
              title="Mark all as read"
            >
              {unseenCount} new
            </button>
          )}
          {canManage && (
            <div className="kb-col-menu-wrapper" ref={menuRef}>
              <button
                className="kb-col-menu-trigger"
                onClick={() => setMenuOpen(!menuOpen)}
                title="Column options"
              >
                <span /><span /><span />
              </button>
              {menuOpen && (
                <div className="kb-col-menu">
                  <button onClick={handleInsertBefore}>Insert Column Before</button>
                  <button onClick={handleInsertAfter}>Insert Column After</button>
                  <div className="kb-col-menu-divider" />
                  <button onClick={handleConfigureFields}>Configure card fields…</button>
                  <div className="kb-col-menu-divider" />
                  <button onClick={handleClearCards} disabled={totalCount === 0}>
                    Remove Cards
                  </button>
                  {currentBoard.columns.length > 1 && (
                    <button className="kb-col-menu-danger" onClick={handleDelete}>
                      Delete Column
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {showFieldsModal && (
        <ColumnFieldsConfigModal
          column={column}
          onClose={() => setShowFieldsModal(false)}
          onSave={handleSaveFields}
        />
      )}
    </div>
  )
}

ColumnHeader.propTypes = {
  column: PropTypes.object.isRequired,
  headerRef: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  unseenCount: PropTypes.number.isRequired,
  searchActive: PropTypes.bool
}

export default ColumnHeader
